import {useGraphql} from "@/core/graphql/service";
import parseTime from "@/core/util/date/parseTime";
import {DeliveryCalculate, DeliveryCalculateInput} from "@/gql/gen";
import vorderDeliveryCalculateMutation from "@/gql/gen/mutation/vorderDeliveryCalculateMutation";
import {OrderProfileModel} from "@/modules/shop/composable/util";
import {useVorderApi} from "@/modules/shop/store/vorder-api";
import {useVorderState} from "@/modules/shop/store/vorder-state";
import {defineStore, storeToRefs} from "pinia";
import {computed} from "vue";
import {useTime} from "@/core/store/time";

const STORE_NAME = 'shop-delivery'

export const useDeliveryStore = defineStore(STORE_NAME, () => {

    const {useMutation, responseSelection} = useGraphql();

    const vorderApi = useVorderApi()
    const vorderState = useVorderState()

    const {updateAttrs} = vorderState
    const {vorderInput} = storeToRefs(vorderApi)
    const {
        deliveryCalculateResult,
        attrs,
        currentProfileId,
        deliveryPriceRules,
        deliveryDate,
        deliveryDateFormatted,
    } = storeToRefs(vorderState)

    const profileCalculate = async (profile: OrderProfileModel) => {
        /*
        const {success, payload} = await profileCalculateRequest.mutate({
            profileId: profile.id,
            attrs: profile.getRawAttrValues(),
            timeMode: 'nearest'
        })
        if (success !== false) {
            if (payload) {
                if (payload.RES_STATUS === 'success') {

                    const tag = 'profile.' + (profile.id ? profile.id : 'new')
                    const locationHash = getOrderProfileDeliveryLocationHash(profile.source)
                    if (locationHash) {
                        setDeliveryCalcResult(
                            locationHash,
                            tag,
                            profile.source.ATTR as any,
                            payload
                        )
                    }

                }
            }
        }
        */
    }

    const vorderCalculateRequest = useMutation(
        vorderDeliveryCalculateMutation({
            payload: {
                calc: {
                    __fragment: "DeliveryCalculateFields",
                },
                vorder: {
                    __fragment: "VorderCurrentFields",
                },
            },
            ...responseSelection(),
        })
    );

    const updateDeliveryCalculateResult = (fields: Partial<DeliveryCalculate>) => {
        const storeFields = deliveryCalculateResult.value
            ? deliveryCalculateResult.value
            : ({} as DeliveryCalculate);
        Object.assign(storeFields, fields);
        deliveryCalculateResult.value = storeFields;
    };

    const setDeliveryCalculateResult = (calc: DeliveryCalculate) => {
        deliveryCalculateResult.value = calc;
        setDeliveryDatetime(calc.RES_TIME, false)
    };

    const setDeliveryDatetime = (val: any, updateCalc = true) => {
        const datetime = parseTime(val, null, "dayjs");
        if (datetime) {
            const date = datetime.format("DD.MM.YYYY");
            const time = datetime.format("HH:mm");
            attrs.value.DATE = date;
            attrs.value.TIME = time;
            if (updateCalc)
                updateDeliveryCalculateResult({
                    RES_STATUS: "success",
                });
        }
    };

    const deliveryCalculate = async (calcInput?: DeliveryCalculateInput) => {
        const {success, payload} = await vorderCalculateRequest.mutate({
            order: vorderInput.value,
            calc: calcInput,
        });
        if (success !== false && payload) {
            const {calc} = payload;
            if (calc) {
                if (calc.RES_STATUS === "success") {
                    setDeliveryCalculateResult(calc)
                }
                return calc;
            }
        }
    };

    function applyDeliveryCourier(
        profileModel?: OrderProfileModel | null,
        changed = false
    ) {
        attrs.value.TRANSPORT_TYPE = "courier";
        if (profileModel) {
            if (currentProfileId.value !== profileModel.getId() || changed) {
                const profile = profileModel.source;
                attrs.value["PROFILE_ID"] = profile.ID;
                updateAttrs(profileModel.getRawAttrValues());
                //deliveryStore.clearResults()
            }
        }
    }

    function applyDeliveryPickup(officeId?: number) {
        attrs.value.TRANSPORT_TYPE = "pickup";
        if (officeId) {
            attrs.value.PICKUP_DEPARTMENT = officeId;
        }
    }

    const time = useTime()

    const deliveryCalculateResultAge = computed(() => {
        let res = Infinity
        if (deliveryCalculateResult.value) {
            const requestTime = parseTime(deliveryCalculateResult.value.REQUEST_TIME, null, 'ts_unix');
            if (requestTime)
                res = Math.round(time.timestampIntervalMinute / 1000) - requestTime
        }
        return res
    })

    const deliveryCalculateResultSuccess = computed(() => {
        return deliveryCalculateResult.value?.RES_STATUS === 'success'
    })

    const deliveryCalculateResultActual = computed(() => {
        return deliveryCalculateResultAge.value && deliveryCalculateResultAge.value < 60 * 5
    })

    const deliveryCalculateResultSuccessAndActual = computed(() => {
        return deliveryCalculateResultSuccess.value && deliveryCalculateResultActual.value
    })

    const nearestAvailableTime = computed(() => {
        if (deliveryCalculateResult.value?.RES_STATUS === 'time_busy') {
            return parseTime(deliveryCalculateResult.value.RES_TIME, null, 'dayjs');
        }
    })

    const deliveryCalculateResultReset = () => {
        deliveryCalculateResult.value = null
    }

    const deliveryPriceRule = computed(() => {
        const date = deliveryDate.value?.format('DD.MM.YYYY')
        const ruleDefault = deliveryPriceRules.value.find(rule => !rule.DATE)
        const ruleByDate = deliveryPriceRules.value.find(rule => rule.DATE ? rule.DATE === date : false)
        return ruleByDate || ruleDefault
    })

    const deliveryPrice = computed(() => {
        if (attrs.value.TRANSPORT_TYPE === 'pickup') {
            return 0
        }
        return deliveryPriceRule.value?.PRICE || 0
    })

    return {
        profileCalculate,
        applyDeliveryCourier,
        applyDeliveryPickup,
        deliveryCalculate,
        vorderCalculateRequest,
        setDeliveryDatetime,
        deliveryCalculateResult,
        deliveryCalculateResultAge,
        deliveryCalculateResultActual,
        deliveryCalculateResultReset,
        deliveryCalculateResultSuccess,
        deliveryCalculateResultSuccessAndActual,
        nearestAvailableTime,
        setDeliveryCalculateResult,
        deliveryPrice
    }
})


